"use client"

import { useEffect, useState } from "react"
import { useRouter } from "next/navigation"
import { useTranslations } from "next-intl"
import { Loader2 } from "lucide-react"
import { createClient } from "@/utils/supabase/client"
import { Button } from "@/components/ui/button"
import { useMobile } from "@/hooks/use-mobile"
import { useCartContext } from "@/components/cart/cart-provider"
import { CheckoutForm, isShippingFormComplete } from "@/components/cart/checkout-form"
import PayPalCheckout from "@/components/cart/paypal-checkout"
import { buildCheckoutIntent } from "@/lib/payments/build-checkout-intent"
import { CHECKOUT_COUNTDOWN_SECONDS } from "@/lib/config"
import { CountdownTimer } from "@/components/cart/countdown-timer"
import { OutOfStockModal } from "@/components/cart/out-of-stock-modal"
import { OrderPlaced } from "@/components/checkout/order-placed"
import { useCheckoutSession } from "@/hooks/use-checkout-session"
import { deriveShippingMode } from "@/lib/checkout/derive-shipping-mode"
import { aggregateParcel } from "@/lib/checkout/aggregate-parcel"
import { ShippingSelector } from "@/components/cart/shipping-selector"
import type { ShippingFormData } from "@/components/cart/shipping-form"
import { clearShippingDraft } from "@/components/cart/shipping-form"
import { getOrderByPaymentId, type EnrichedOrder } from "@/lib/adapters/orders"
import { releaseCart } from "@/lib/cart-quick-checkout"
import { linkOrderToMessage } from "@/lib/chat/link-order"

type Props = {
  open: boolean
  onOpenChangeAction: (open: boolean) => void
  userId: string
  roomName: string
  messageId: string
  listingId: string
  embedded?: boolean
  addedInThisFlow?: boolean
  hideShippingSelection?: boolean
}

export function ChatCheckoutDialog({
  open,
  onOpenChangeAction,
  userId,
  roomName,
  messageId,
  listingId,
  hideShippingSelection = false,
}: Props) {
  const router = useRouter()
  const checkoutT = useTranslations("checkout")
  const isMobile = useMobile()
  const { loadCart } = useCartContext()
  const [isProcessing, setIsProcessing] = useState(false)
  const [viewState, setViewState] = useState<"checkout" | "confirmation">("checkout")
  const [placedOrder, setPlacedOrder] = useState<EnrichedOrder | null>(null)
  const [checkoutProfile, setCheckoutProfile] = useState<{ id: string; email?: string; phone?: string } | null>(null)

  const {
    items,
    currencyCode,
    subtotal,
    shippingTotal,
    serviceFee,
    total,
    allShippingMethodsSelected,
    hasUnavailableItems,
    sellerDetails,
    isLoading: isPreparing,
    isValidating,
    validationError,
    clearValidationError,
    buildPayload,
    isCountryBannedForShipping,
    updateShippingMethod,
  } = useCheckoutSession({ scopeListingId: listingId, userId, enabled: open })

  useEffect(() => {
    if (!open) {
      setPlacedOrder(null)
      setViewState("checkout")
      setCheckoutProfile(null)
    }
  }, [open])

  useEffect(() => {
    if (!open || !userId) {
      setCheckoutProfile(null)
      return
    }
    let cancelled = false
    const supabase = createClient()
    ;(async () => {
      const [{ data: authData }, { data: profileData }] = await Promise.all([
        supabase.auth.getUser(),
        supabase.from("profiles").select("id, email, phone").eq("id", userId).maybeSingle(),
      ])
      if (cancelled) return
      setCheckoutProfile({
        id: userId,
        email: profileData?.email ?? authData.user?.email ?? undefined,
        phone: profileData?.phone ?? undefined,
      })
    })()
    return () => {
      cancelled = true
    }
  }, [open, userId])

  const close = async () => {
    onOpenChangeAction(false)
    setPlacedOrder(null)
    setViewState("checkout")
    await releaseCart({ listingIds: [listingId] }).catch(() => {})
  }

  const completeOrder = async (_data: ShippingFormData, paypalData?: { orderID: string; captureIds?: Record<string, string>; orderNumber?: string; orderId?: string }) => {
    if (paypalData?.orderNumber) {
      console.log('[CHAT-CHECKOUT] Order created server-side:', paypalData.orderNumber)

      setPlacedOrder(null)
      clearShippingDraft()
      setViewState("confirmation")
      setIsProcessing(false)

      try {
        const order = await getOrderByPaymentId(paypalData.orderID)
        if (!order) {
          throw new Error(`Order not found for PayPal order ${paypalData.orderID}`)
        }
        setPlacedOrder(order)
      } catch (error) {
        console.error('[CHAT-CHECKOUT] Failed to hydrate placed order:', error)
        await close()
        router.push("/orders-placed")
        return
      }

      if (roomName && messageId) {
        await linkOrderToMessage({ roomName, messageId, orderNumber: paypalData.orderNumber })
      }
      await loadCart(true)
      return
    }

    console.warn('[CHAT-CHECKOUT] Payment captured but orderNumber missing — reconciliation will create the order. PayPal order:', paypalData?.orderID)
    await loadCart(true)
    await close()
  }

  const disabled = isPreparing || isProcessing || isValidating || items.length === 0 || hasUnavailableItems || !allShippingMethodsSelected || validationError?.errorCode === 'SELLER_PAYMENT_ERROR'

  const errorModal = validationError ? (
    <OutOfStockModal
      open={!!validationError}
      onCloseAction={() => {
        clearValidationError()
        close()
      }}
      listingTitles={validationError.titles}
      errorCode={validationError.errorCode}
    />
  ) : null

  const needsShippingSelection =
    !hideShippingSelection && items.some((i) => i.shippingMethods.length > 1 && !i.selectedShippingMethod)

  const confirmationBody = (
    <div className="flex min-h-0 flex-1 flex-col dark">
      {placedOrder ? (
        <OrderPlaced
          order={placedOrder}
          onContinue={() => {
            close()
            router.push("/orders-placed")
          }}
          onContact={() => {
            close()
            setTimeout(() => router.push("/contact"), 200)
          }}
        />
      ) : (
        <div className="flex min-h-[320px] flex-col items-center justify-center">
          <Loader2 className="mb-4 h-8 w-8 animate-spin text-neutral-400" />
          <p className="text-sm text-neutral-400">{checkoutT("processing")}</p>
        </div>
      )}
    </div>
  )

  const checkoutBody = (
    <div data-report-dialog className="flex min-h-0 flex-1 flex-col">
      {isPreparing ? (
        <div className="flex items-center justify-center min-h-[320px]">
          <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-[240px] gap-4 px-4 text-neutral-400">
          <p className="text-sm text-center">No items to checkout.</p>
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => close()}>
            Close
          </Button>
        </div>
      ) : (
        <div className="min-h-0 flex-1">
          {needsShippingSelection && (
            <div className="px-4 py-3 border-b border-neutral-800">
              <p className="text-xs text-amber-400 mb-2">Select shipping method:</p>
              {items
                .filter((i) => i.shippingMethods.length > 1)
                .map((item) => (
                  <div key={item.id} className="mb-2 last:mb-0">
                    <p className="text-xs text-neutral-400 mb-1 truncate">{item.name}</p>
                    <ShippingSelector
                      itemId={item.id}
                      shippingMethods={item.shippingMethods}
                      selectedShippingMethod={item.selectedShippingMethod}
                      currencyCode={currencyCode}
                      onSelectShippingAction={updateShippingMethod}
                    />
                  </div>
                ))}
            </div>
          )}
          <CheckoutForm
            className="bg-sheet text-foreground"
            embedded
            hideShippingAddressTitle
            inDarkSheetBody={isMobile}
            sellerCheckoutNote={items.map((i) => i.sellerCheckoutNote?.trim()).find(Boolean) ?? null}
            subtotal={subtotal}
            shippingTotal={shippingTotal}
            serviceFee={serviceFee}
            total={total}
            currencyCode={currencyCode}
            onCompleteAction={(data) => {
              setIsProcessing(true)
              completeOrder(data).finally(() => setIsProcessing(false))
            }}
            userId={userId}
            profile={checkoutProfile ?? { id: userId }}
            renderPaymentAction={(formData, selectedCountry, ctx) => {
              // WS4.5 — per-seller Shippo routing. Single-seller invariant
              // (PLAN §J.5) guarantees `items` belongs to one seller, so
              // `deriveShippingMode` evaluates the homogeneous branch. Plain
              // (not `useMemo`) — `renderPaymentAction` is itself a callback
              // re-invoked per parent render; surrounding scope doesn't
              // memoize either. See .tondocs/skills/hex.md §1 — these are
              // pure-domain reads forwarded to the delivery shell.
              const shippingMode = deriveShippingMode(items)
              const parcel = shippingMode === "live_rate"
                ? aggregateParcel(
                    items.map((i) => ({
                      quantity: i.quantity,
                      parcelWeight: i.parcelWeight,
                      parcelLength: i.parcelLength,
                      parcelWidth: i.parcelWidth,
                      parcelHeight: i.parcelHeight,
                      massUnit: i.parcelMassUnit,
                      distanceUnit: i.parcelDistanceUnit,
                    })),
                  ) ?? undefined
                : undefined
              return sellerDetails.length === 0 ? (
                <p className="text-sm text-neutral-400 py-2">No payment options.</p>
              ) : (
                <PayPalCheckout
                  intent={formData && items[0]?.sellerId ? buildCheckoutIntent(
                    items,
                    formData,
                    currencyCode,
                    Object.fromEntries(items.map((i) => [i.id, { kind: "listing" as const }])),
                    items[0].sellerId,
                  ) : null}
                  totalAmount={ctx.effectiveTotal}
                  sellerDetails={sellerDetails}
                  formData={formData}
                  selectedCountry={selectedCountry}
                  currencyCode={currencyCode}
                  disabled={disabled || !isShippingFormComplete(formData) || (formData ? isCountryBannedForShipping(formData.country) : false)}
                  isCountryBannedForShipping={isCountryBannedForShipping}
                  shippingMode={shippingMode}
                  parcel={parcel}
                  onLiveRateAmountChange={ctx.onLiveRateAmountChange}
                  getCheckoutPayload={(extras) => {
                    if (!formData) return null
                    return buildPayload(formData, extras?.externalShipping ?? null)
                  }}
                  onApproveAction={(paypalData) => {
                    if (!formData) return
                    setIsProcessing(true)
                    completeOrder(formData, paypalData)
                  }}
                />
              )
            }}
          />
        </div>
      )}
    </div>
  )

  return (
    <>
      {errorModal}
      {viewState === "confirmation" ? confirmationBody : (
        <>
          {!isPreparing && items.length > 0 ? (
            <div className="sticky top-0 z-30 shrink-0 bg-neutral-900 border-b border-neutral-800">
              <CountdownTimer
                initialSeconds={CHECKOUT_COUNTDOWN_SECONDS}
                onExpire={() => close()}
                className="border-0 rounded-none"
              />
            </div>
          ) : null}
          {checkoutBody}
        </>
      )}
    </>
  )
}
